/**
 * Mini Cart PitchPrint Previews - PRODUCTION READY VERSION
 * Clean version with edit button commented out and enhanced property hiding
 * File: assets/mini-cart-pitchprint-previews-fixed.js
 */

(() => {
  'use strict';

  let initialized = false;
  let cartIsOpen = false;
  let processedItems = new Set();
  const stateManager = window.PitchPrintStateManager;

  // Debounce timer for cart processing - prevents flickering when Shopify
  // section rendering replaces the cart drawer DOM after add-to-cart.
  // All triggers (MutationObserver, cartdrawer:opened, cart:updated) collapse
  // into a single processing call after the DOM stabilizes.
  let processDebounceTimer = null;
  const PROCESS_DEBOUNCE_MS = 600;

  const debouncedProcessCartItems = (invalidateCache = false) => {
    if (invalidateCache) {
      invalidateCartCache();
    }
    if (processDebounceTimer) {
      clearTimeout(processDebounceTimer);
      log('⏱️ Debounce reset - waiting for DOM to stabilize...');
    }
    processDebounceTimer = setTimeout(() => {
      processDebounceTimer = null;
      log('⏱️ Debounce complete - processing cart items now');
      processCartItems();
    }, PROCESS_DEBOUNCE_MS);
  };

  // Use global debug flag from theme settings
  const log = (...args) => window.PITCHPRINT_DEBUG && console.log('[MiniCartPreviewsFixed]', ...args);

  // === ASPECT RATIO DETECTION ===
  // Detects the aspect ratio shape class based on image natural dimensions
  // Returns: 'tall-shape', 'very-tall-shape', 'wide-shape', or 'dynamic-ratio'
  const detectAspectRatioClass = (img) => {
    if (!img || !img.naturalWidth || !img.naturalHeight) {
      log('Cannot detect aspect ratio - image not loaded or no natural dimensions');
      return 'dynamic-ratio';
    }

    const width = img.naturalWidth;
    const height = img.naturalHeight;
    const ratio = width / height;

    log('🔍 Detecting aspect ratio:', { width, height, ratio: ratio.toFixed(3) });

    // Classify based on aspect ratio
    // ratio > 1 means wider than tall
    // ratio < 1 means taller than wide
    // ratio = 1 means square

    if (ratio > 1.3) {
      // Wide/horizontal shapes (spirit flags 2x3, 3x5, backdrops 8x10, etc.)
      log('📐 Wide shape detected (ratio > 1.3)');
      return 'wide-shape';
    } else if (ratio < 0.35) {
      // Very tall shapes (feather flags ~1:4+)
      log('📐 Very tall shape detected (ratio < 0.35)');
      return 'very-tall-shape';
    } else if (ratio < 0.6) {
      // Tall shapes (banners, longboards, popup stands ~1:2.5)
      log('📐 Tall shape detected (ratio < 0.6)');
      return 'tall-shape';
    } else {
      // Near-square shapes (ratio between 0.6 and 1.3)
      log('📐 Near-square shape detected (0.6 <= ratio <= 1.3)');
      return 'dynamic-ratio';
    }
  };

  // Apply aspect ratio class to preview container and its images
  const applyAspectRatioToContainer = (container, img) => {
    const aspectClass = detectAspectRatioClass(img);
    const shapeClasses = ['tall-shape', 'very-tall-shape', 'wide-shape', 'dynamic-ratio'];

    // Remove existing shape classes from container
    shapeClasses.forEach(cls => container.classList.remove(cls));

    // Add new aspect ratio class
    container.classList.add(aspectClass);

    log('📐 Applied aspect ratio class to container:', aspectClass);
    return aspectClass;
  };

  if (!stateManager) {
    console.error('[MiniCartPreviewsFixed] StateManager not found - mini cart previews disabled');
    return;
  }

  const init = () => {
    if (initialized) {
      log('Already initialized, skipping...');
      return;
    }

    // ⚠️ CRITICAL: Do NOT run on main cart page (/cart)
    // PitchPrint has its own native preview rendering for the main cart
    // This script should ONLY handle mini-cart drawer previews
    if (window.location.pathname === '/cart') {
      log('🚫 Detected main cart page - SKIPPING initialization to avoid conflicts with PitchPrint native rendering');
      return;
    }

    initialized = true;
    log('🎨 Initializing FIXED mini cart previews with StateManager...');

    setupCartListeners();
    setupStateManagerListeners();

    log('✅ Fixed mini cart preview system ready');
  };

  // =====================================
  // CART EVENT LISTENERS
  // =====================================

  const setupCartListeners = () => {
    // Listen for cart drawer opened events
    document.addEventListener('cartdrawer:opened', handleCartOpened);
    document.addEventListener('cart:opened', handleCartOpened);
    document.addEventListener('cart:updated', handleCartUpdated);

    // Detect cart opening by clicking cart icon
    document.addEventListener('click', (e) => {
      const cartTrigger = e.target.closest('[data-cart-drawer-toggle], .cart-icon, .mini-cart-toggle');
      if (cartTrigger) {
        setTimeout(handleCartOpened, 100);
      }
    });

    // CRITICAL FIX: Watch for DOM changes in cart drawer to detect when items are added
    // This handles the case where cart is already open and new items are added
    setupCartDOMObserver();

    log('🎯 Cart event listeners attached');
  };

  const setupStateManagerListeners = () => {
    stateManager.on('cart:preview:updated', handlePreviewUpdated);
    log('📡 StateManager listeners attached');
  };

  // =====================================
  // CART DOM OBSERVER
  // =====================================

  let cartDOMObserver = null;

  const setupCartDOMObserver = () => {
    // Wait for cart drawer to exist in DOM
    const waitForCartDrawer = setInterval(() => {
      const cartDrawer = document.querySelector('cart-drawer, .cart-drawer, [data-cart-drawer], #CartDrawer, mini-cart');

      if (cartDrawer) {
        clearInterval(waitForCartDrawer);

        // Create observer to watch for changes in cart items
        cartDOMObserver = new MutationObserver((mutations) => {
          for (const mutation of mutations) {
            // Check if cart items were added or changed
            if (mutation.type === 'childList' || mutation.type === 'attributes') {
              const addedNodes = Array.from(mutation.addedNodes);

              // CRITICAL FIX: Ignore our own edit button/preview additions to prevent infinite loops
              const isOurOwnAddition = addedNodes.some(node =>
                node.nodeType === 1 && node.matches && (
                  node.matches('.pitchprint-edit-container, .pitchprint-preview-container, .pitchprint-edit-btn-pill') ||
                  node.classList?.contains('pitchprint-edit-container') ||
                  node.classList?.contains('pitchprint-preview-container') ||
                  node.classList?.contains('pitchprint-edit-btn-pill')
                )
              );

              if (isOurOwnAddition) {
                // Ignore mutations caused by our own edit buttons/previews to prevent infinite loops
                continue;
              }

              const hasCartItems = addedNodes.some(node =>
                node.nodeType === 1 && (
                  node.matches && node.matches('[data-variant-id], .cart-item, .mini-cart__item') ||
                  node.querySelector && node.querySelector('[data-variant-id], .cart-item, .mini-cart__item')
                )
              );

              if (hasCartItems) {
                log('🔄 Cart DOM changed - new items detected, debouncing reprocess...');
                debouncedProcessCartItems(true);
                break;
              }
            }
          }
        });

        // Observe cart drawer for changes
        cartDOMObserver.observe(cartDrawer, {
          childList: true,
          subtree: true,
          attributes: false
        });

        log('👀 Cart DOM observer started');
      }
    }, 500);

    // Stop checking after 10 seconds
    setTimeout(() => clearInterval(waitForCartDrawer), 10000);
  };

  // =====================================
  // CART EVENT HANDLERS
  // =====================================

  const handleCartOpened = () => {
    // CRITICAL FIX: Don't skip if cart is already open - we need to reprocess on every open
    // This handles the case where items are added while cart is still visible
    const wasAlreadyOpen = cartIsOpen;
    cartIsOpen = true;

    if (wasAlreadyOpen) {
      log('🛒 Cart already open, debouncing reprocess...');
    } else {
      log('🛒 Cart opened, debouncing process...');
    }

    // Always invalidate cache on cart open to ensure fresh data
    debouncedProcessCartItems(true);
  };

  const handleCartUpdated = () => {
    log('🔄 Cart updated, debouncing refresh...');

    // IMPROVED: Clear processed items but be more selective
    // Only clear items that no longer exist in the cart
    const currentCartItems = document.querySelectorAll('[data-variant-id]');
    const currentItemIds = Array.from(currentCartItems).map(item => getItemIdentifier(item));

    // Remove processed items that are no longer in cart
    for (const processedId of processedItems) {
      if (!currentItemIds.includes(processedId)) {
        processedItems.delete(processedId);
        log(`🗑️ Removed obsolete processed item: ${processedId}`);
      }
    }

    debouncedProcessCartItems(true);
  };

  const handlePreviewUpdated = (event) => {
    const { lineId, previewData } = event.detail;
    log('📡 Preview updated via StateManager:', { lineId, previewData });
  };

  // =====================================
  // CART ITEM PROCESSING
  // =====================================

  // PROBLEM: Cart previews disappear when new items are added
  // SOLUTION: Modify processCartItems function in mini-cart-pitchprint-previews-fixed.js

  // Replace the processCartItems function with this improved version:

  let processRetryCount = 0;
  const MAX_PROCESS_RETRIES = 5;

  const processCartItems = async () => {
    // Enhanced selectors for cart items
    const selectors = [
      'cart-items .mini-cart__navigation li[data-variant-id]',
      '.mini-cart__navigation li[data-variant-id]',
      '.cart-item[data-variant-id]',
      '.cart__item[data-variant-id]',
      '[data-line-item] .cart-item'
    ];

    let cartItems = [];
    for (const selector of selectors) {
      cartItems = document.querySelectorAll(selector);
      if (cartItems.length > 0) {
        log(`🎯 Found ${cartItems.length} cart items using selector: ${selector}`);
        processRetryCount = 0; // Reset retry count on success
        break;
      }
    }

    if (cartItems.length === 0) {
      if (processRetryCount < MAX_PROCESS_RETRIES) {
        processRetryCount++;
        log(`❌ No cart items found, will retry (${processRetryCount}/${MAX_PROCESS_RETRIES})...`);
        setTimeout(processCartItems, 500);
        return;
      } else {
        log(`❌ No cart items found after ${MAX_PROCESS_RETRIES} retries, giving up`);
        processRetryCount = 0;
        return;
      }
    }

    // Process items sequentially (await for each)
    for (let index = 0; index < cartItems.length; index++) {
      const item = cartItems[index];
      const itemNumber = index + 1;
      const itemId = getItemIdentifier(item);

      // IMPROVED: Check if preview container AND edit button exist
      const hasExistingPreview = item.querySelector('.pitchprint-preview-container');
      const hasExistingEditButton = item.querySelector('.pitchprint-edit-btn-pill');

      // IMPORTANT: Get cart item data (handles both PitchPrint and file uploads)
      // Pass the DOM index to correctly match multiple items with same variant ID
      const cartItemData = await getCartItemData(item, index, cartItems);

      if (!cartItemData) {
        log(`📝 No PitchPrint or file upload data found for item ${itemNumber}: ${itemId}`);
        continue;
      }

      log(`✅ Processing item ${itemNumber}: ${itemId} - Type: ${cartItemData.type}, ID: ${cartItemData.id}`);

      // Add preview if missing
      if (!hasExistingPreview) {
        log(`🎨 Adding ${cartItemData.type} preview for item ${itemNumber}`);
        addPreviewToItem(item, cartItemData, itemNumber);
      } else {
        log(`✅ Item ${itemNumber} already has preview`);
      }

      // Add edit button ONLY for PitchPrint projects (not file uploads)
      if (cartItemData.hasEditButton && !hasExistingEditButton) {
        log(`🔘 Adding edit button for PitchPrint item ${itemNumber}`);
        addEditButtonToItem(item, cartItemData.id, item.dataset.variantId, itemNumber);
      } else if (!cartItemData.hasEditButton) {
        log(`⏭️ Skipping edit button for file upload item ${itemNumber}`);
      } else {
        log(`✅ Item ${itemNumber} already has edit button`);
      }

      // Update processed items tracking
      processedItems.add(itemId);

      // Notify StateManager
      stateManager.setCartPreview(itemId, {
        type: cartItemData.type,
        id: cartItemData.id,
        processed: true,
        timestamp: Date.now()
      });
    }

    // Hide PitchPrint properties from customer view
    setTimeout(() => {
      hidePitchPrintProperties();
    }, 100);
  };


  // =====================================
  // CART API CACHE
  // =====================================

  let cartDataCache = null;
  let cartDataTimestamp = 0;
  const CART_CACHE_TTL = 2000; // 2 seconds

  const fetchCartData = async () => {
    const now = Date.now();

    // Return cached data if still fresh
    if (cartDataCache && (now - cartDataTimestamp) < CART_CACHE_TTL) {
      log('💾 Using cached cart data');
      return cartDataCache;
    }

    try {
      log('🌐 Fetching cart data from Shopify Cart API...');
      const response = await fetch('/cart.js');

      if (!response.ok) {
        throw new Error(`Cart API returned ${response.status}`);
      }

      const cartData = await response.json();
      cartDataCache = cartData;
      cartDataTimestamp = now;

      log(`✅ Cart data fetched: ${cartData.items.length} items`);
      return cartData;
    } catch (error) {
      log('❌ Error fetching cart data:', error);
      return null;
    }
  };

  const invalidateCartCache = () => {
    log('🗑️ Invalidating cart cache');
    cartDataCache = null;
    cartDataTimestamp = 0;
  };

  // =====================================
  // ENHANCED CART ITEM DETECTION (CART API)
  // Returns object with type info to avoid data contamination
  // =====================================

  const getCartItemData = async (cartItem, domIndex, allCartItems) => {
    log('🔍 Cart item detection starting (Cart API method)...');

    // Get variant ID from cart item DOM element
    const variantId = cartItem.dataset.variantId ||
                      cartItem.dataset.lineId ||
                      cartItem.querySelector('[data-variant-id]')?.dataset.variantId;

    if (!variantId) {
      log('❌ No variant ID found in cart item');
      return null;
    }

    log(`🔍 Looking up variant ${variantId} in Cart API...`);

    // Fetch cart data from Shopify API
    const cartData = await fetchCartData();

    if (!cartData || !cartData.items) {
      log('❌ Cart data not available');
      return null;
    }

    // CRITICAL FIX: Handle multiple items with same variant ID
    // Filter all line items matching this variant ID (not just first one)
    const matchingLineItems = cartData.items.filter(item =>
      String(item.variant_id) === String(variantId) ||
      String(item.id) === String(variantId)
    );

    if (matchingLineItems.length === 0) {
      log(`❌ No line item found for variant ${variantId}`);
      return null;
    }

    // If multiple items with same variant, use DOM index to match correct line item
    let lineItem;
    if (matchingLineItems.length > 1) {
      // FIXED: Count how many items BEFORE this one in the DOM have the same variant ID
      // This gives us the correct index within the matching line items
      let indexWithinVariant = 0;
      for (let i = 0; i < domIndex; i++) {
        const prevItem = allCartItems[i];
        const prevVariantId = prevItem.dataset.variantId ||
                              prevItem.dataset.lineId ||
                              prevItem.querySelector('[data-variant-id]')?.dataset.variantId;
        if (String(prevVariantId) === String(variantId)) {
          indexWithinVariant++;
        }
      }

      log(`🔍 Found ${matchingLineItems.length} line items with variant ${variantId}, using index ${indexWithinVariant} (DOM position ${domIndex})`);

      // Use calculated index to get corresponding line item
      lineItem = matchingLineItems[indexWithinVariant] || matchingLineItems[0];
    } else {
      lineItem = matchingLineItems[0];
    }

    log('📦 Line item found:', {
      title: lineItem.title,
      variant_id: lineItem.variant_id,
      properties: lineItem.properties
    });

    // IMPORTANT: Check for FILE UPLOAD first (separate path from PitchPrint)
    if (lineItem.properties) {
      const uploadId = lineItem.properties['_file_upload_id'];
      if (uploadId && isValidUploadId(uploadId)) {
        // This is a FILE UPLOAD item (NOT a PitchPrint project)
        log(`✅ Found file upload item in Cart API: ${uploadId}`);

        // Get preview URLs from cart properties (not StateManager)
        const frontUrl = lineItem.properties['_file_upload_front_preview'];
        const backUrl = lineItem.properties['_file_upload_back_preview'];

        return {
          type: 'file_upload',
          id: uploadId,
          sides: lineItem.properties['_file_upload_sides'] || 'single',
          previews: {
            front: frontUrl || null,
            back: backUrl || null
          },
          properties: lineItem.properties,
          hasEditButton: false // File uploads cannot be edited
        };
      }

      // Pre-designed (wishlist "as shown") — an exact PitchPrint preview URL is
      // attached. Render it natively so pre-designed lines look like every other
      // PitchPrint item and work with the 2-sided order bump (back falls back to
      // front via the order-bump path in loadPreviewImages).
      const predesignPreview = lineItem.properties['_pp_design_preview'];
      if (predesignPreview) {
        log(`✅ Found pre-designed item in Cart API: ${predesignPreview}`);
        return {
          type: 'pitchprint',
          id: lineItem.properties['_pp_design_collection'] || 'predesigned',
          previews: { front: predesignPreview, back: null },
          properties: lineItem.properties,
          hasEditButton: false
        };
      }

      // Check for PitchPrint project ID
      const pitchprintId = lineItem.properties['_pitchprint'];
      if (pitchprintId && isValidProjectId(pitchprintId)) {
        log(`✅ Found PitchPrint project in Cart API: ${pitchprintId}`);
        return {
          type: 'pitchprint',
          id: pitchprintId,
          previews: null, // Will construct S3 URLs
          hasEditButton: true // PitchPrint projects can be edited
        };
      }
    }

    log('❌ No PitchPrint or file upload data found');
    return null;
  };

  // Fallback DOM-based detection (original method)
  const getProjectIdFromDOM = (cartItem) => {
    // Strategy 1: Check data attributes
    const dataKeys = ['pitchprintProjectId', 'projectId', 'pprintId', 'uploadId', 'fileUploadId'];
    for (let key of dataKeys) {
      const value = cartItem.dataset[key];
      if (value && (isValidProjectId(value) || isValidUploadId(value))) {
        log(`✅ Found ID in data-${key}: ${value}`);
        return value;
      }
    }

    // Strategy 2: Check hidden inputs
    const hiddenInputs = cartItem.querySelectorAll('input[type="hidden"]');
    for (let input of hiddenInputs) {
      const value = input.value || '';
      const name = input.name || '';

      if ((name.includes('file_upload') || name.includes('upload_id')) && value) {
        const uuidMatch = value.match(/([a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12})/i);
        if (uuidMatch && isValidUploadId(uuidMatch[1])) {
          return uuidMatch[1];
        }
      }

      if ((name.includes('pitchprint') || name.includes('_pitchprint')) && value) {
        const match = value.match(/([a-f0-9]{32,40})/);
        if (match && isValidProjectId(match[1])) {
          return match[1];
        }
      }
    }

    log('❌ No project ID found in DOM fallback');
    return null;
  };

  const isValidProjectId = (id) => {
    return typeof id === 'string' &&
           id.length >= 16 &&
           /^[a-zA-Z0-9]+$/.test(id);
  };

  const isValidUploadId = (id) => {
    // Upload IDs are UUIDs (format: xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx)
    return typeof id === 'string' &&
           /^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/i.test(id);
  };

  const getItemIdentifier = (item) => {
    return item.dataset.variantId || 
           item.dataset.lineId || 
           item.dataset.key ||
           item.querySelector('[data-variant-id]')?.dataset.variantId ||
           `item-${Array.from(item.parentNode.children).indexOf(item)}`;
  };

  // =====================================
  // PREVIEW CREATION WITH PROPER STRUCTURE
  // =====================================

  const addPreviewToItem = (cartItem, cartItemData, itemNumber) => {
    log(`🎨 Adding ${cartItemData.type} preview to item ${itemNumber} (ID: ${cartItemData.id})`);

    // Find image container in cart item
    const imageContainer = findImageContainer(cartItem);
    if (!imageContainer) {
      log(`❌ No image container found for item ${itemNumber}`);
      return;
    }

    // CRITICAL FIX: Check if preview already exists for this SAME item
    const existingPreview = cartItem.querySelector('.pitchprint-preview-container');
    if (existingPreview) {
      const existingItemId = existingPreview.dataset.itemId;
      const existingItemType = existingPreview.dataset.itemType;

      // If it's the same item and type, don't remove it (prevents removing edit buttons)
      if (existingItemId === cartItemData.id && existingItemType === cartItemData.type) {
        log(`✅ Preview already exists for same item ${itemNumber} (${cartItemData.type}: ${cartItemData.id}), skipping`);
        return;
      }

      // Different item/type - remove old preview
      log(`🔄 Removing existing preview for item ${itemNumber} (different item: ${existingItemType}:${existingItemId} → ${cartItemData.type}:${cartItemData.id})`);
      existingPreview.remove();
    }

    // Detect product characteristics
    const isCircle = detectCircleShape(cartItem);
    // For file uploads, use explicit sides property from cart; fall back to DOM text detection.
    // Order-bumped items have stale _file_upload_sides='single' — always use DOM detection for those.
    const isOrderBumped = cartItemData.type === 'file_upload' &&
      cartItemData.properties && cartItemData.properties._order_bumped === 'sides_upgrade';
    const isSingleSided = (cartItemData.sides && !isOrderBumped)
      ? cartItemData.sides === 'single'
      : detectSingleSided(cartItem);

    // Create proper preview container structure
    const container = document.createElement('div');
    container.className = 'pitchprint-preview-container';
    container.dataset.currentSide = '1';
    container.dataset.itemType = cartItemData.type; // Store type instead of conflating with projectId
    container.dataset.itemId = cartItemData.id;
    container.dataset.itemNumber = itemNumber;
    container.dataset.singleSided = isSingleSided;

    // Add shape and sided classes
    if (isCircle) {
      container.classList.add('circle-shape');
    }
    if (isSingleSided) {
      container.classList.add('single-sided');
    }

    // Create preview cards container
    const previewCards = document.createElement('div');
    previewCards.className = 'preview-cards';

    // Create front card (always needed)
    const frontCard = document.createElement('div');
    frontCard.className = 'preview-card preview-card-front front';
    const frontImg = document.createElement('img');
    frontImg.style.cssText = 'width: 100%; height: 100%; object-fit: cover; display: block;';
    frontImg.alt = 'Design Side 1';
    frontCard.appendChild(frontImg);

    // Create back card only for double-sided variants
    let backCard = null;
    let backImg = null;
    if (!isSingleSided) {
      backCard = document.createElement('div');
      backCard.className = 'preview-card preview-card-back back';
      backImg = document.createElement('img');
      backImg.style.cssText = 'width: 100%; height: 100%; object-fit: cover; display: block;';
      backImg.alt = 'Design Side 2';
      backCard.appendChild(backImg);
    }

    // Assemble structure
    previewCards.appendChild(frontCard);
    if (backCard) {
      previewCards.appendChild(backCard);
    }
    container.appendChild(previewCards);

    // Front/Back flip badge (double-sided only) — updated by setupToggle on click
    if (!isSingleSided) {
      const sideBadge = document.createElement('div');
      sideBadge.className = 'preview-side-badge';
      sideBadge.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 256 256" fill="currentColor" aria-hidden="true"><path d="M240,56v48a8,8,0,0,1-8,8H184a8,8,0,0,1,0-16H211.4L184.81,71.64l-.25-.24a80,80,0,1,0-1.67,114.78,8,8,0,0,1,11,11.63A95.44,95.44,0,0,1,128,224h-1.32A96,96,0,1,1,195.75,60L224,85.8V56a8,8,0,1,1,16,0Z"></path></svg><span>Front</span>';
      container.appendChild(sideBadge);
    }

    // Ensure container has relative positioning
    if (getComputedStyle(imageContainer).position === 'static') {
      imageContainer.style.position = 'relative';
    }

    imageContainer.appendChild(container);
    log(`✅ Preview container added to item ${itemNumber}`);

    // Load preview images (handles both PitchPrint and file uploads)
    loadPreviewImages(container, frontImg, backImg, cartItemData, itemNumber, isSingleSided);
  };

  const findImageContainer = (item) => {
    const selectors = [
      '.cart-item__media img',
      '.cart__item-image img', 
      '.mini-cart__image img',
      '.cart-drawer__image img',
      '.product-image img',
      'img[src*="product"]',
      '.cart-item img',
      'img'
    ];

    for (const selector of selectors) {
      const img = item.querySelector(selector);
      if (img) {
        const parent = img.parentElement;
        parent.style.position = 'relative';
        return parent;
      }
    }

    return null;
  };

  // =====================================
  // CORRECT S3 URL LOADING
  // =====================================

  const loadPreviewImages = (container, frontImg, backImg, cartItemData, itemNumber, isSingleSided) => {
    log(`🌐 Loading ${cartItemData.type} preview for item ${itemNumber}`);

    let side1Url, side2Url;
    const timestamp = Date.now();

    if (cartItemData.type === 'file_upload') {
      // File upload: Use preview URLs from cart properties (already stored)
      side1Url = cartItemData.previews.front;
      side2Url = cartItemData.previews.back;
      // Order-bumped: no back artwork exists — mirror front preview
      if (!side2Url && !isSingleSided && cartItemData.properties &&
          cartItemData.properties._order_bumped === 'sides_upgrade') {
        side2Url = side1Url;
        log(`🔄 Order-bumped file upload — mirroring front to back`);
      }
      log(`🌐 File Upload URLs from cart properties:`, { side1Url, side2Url });
    } else if (cartItemData.type === 'pitchprint') {
      if (cartItemData.previews && cartItemData.previews.front) {
        // Pre-designed (wishlist "as shown"): an EXACT preview URL is supplied
        // (specific collection page) — use it instead of the project _1/_2 build.
        side1Url = cartItemData.previews.front;
        side2Url = cartItemData.previews.back;
        // Mirror front→back only when we KNOW the back equals the front:
        // pre-designed "as shown" (single design, no separate back) or the
        // "Go Double Sided" order bump. A custom 2-sided design has a distinct
        // back, so it must NOT be mirrored (it uses the _1/_2 project path).
        const backEqualsFront = cartItemData.properties &&
          (cartItemData.properties._pp_predesigned === '1' ||
           cartItemData.properties._order_bumped === 'sides_upgrade');
        if (!side2Url && !isSingleSided && backEqualsFront) {
          side2Url = side1Url;
        }
        log(`🌐 Pre-designed PitchPrint URLs:`, { side1Url, side2Url });
      } else {
        // PitchPrint project: Construct S3 URLs for previews
        side1Url = `https://s3-eu-west-1.amazonaws.com/pitchprint.io/previews/${cartItemData.id}_1.jpg?t=${timestamp}`;
        side2Url = !isSingleSided ? `https://s3-eu-west-1.amazonaws.com/pitchprint.io/previews/${cartItemData.id}_2.jpg?t=${timestamp}` : null;
        log(`🌐 PitchPrint URLs:`, { side1Url, side2Url });
      }
    } else {
      log(`❌ Unknown item type: ${cartItemData.type}`);
      side1Url = null;
      side2Url = null;
    }

    // Load front image (side 1) - always needed
    frontImg.onload = () => {
      log(`✅ Side 1 loaded successfully for item ${itemNumber}`);
      container.classList.add('loaded');

      // Add class to parent container to hide default product image
      const imageContainer = container.parentElement;
      if (imageContainer) {
        imageContainer.classList.add('has-pitchprint-preview');
        log(`✅ Added has-pitchprint-preview class to parent container`);
      }

      // Apply aspect ratio class based on image dimensions
      applyAspectRatioToContainer(container, frontImg);

      if (!isSingleSided) {
        setupToggle(container, itemNumber);
      }
    };

    frontImg.onerror = () => {
      log(`❌ Side 1 failed for item ${itemNumber}`);
      // Don't set fallback src - just mark as loaded so UI doesn't hang
      container.classList.add('loaded');
      container.classList.add('error');
    };

    // Load back image (side 2) only for double-sided variants
    if (!isSingleSided && backImg && side2Url) {
      backImg.onerror = () => {
        // No back design yet (e.g. order bump upgrade) — show front on both sides
        log(`⚠️ Side 2 not found for item ${itemNumber} — using front artwork for both sides`);
        backImg.src = side1Url;
      };

      // Start loading back image
      backImg.src = side2Url;
    }

    // Start loading front image
    if (side1Url) {
      frontImg.src = side1Url;
    } else {
      log(`❌ No side1Url available for item ${itemNumber}`);
      container.classList.add('loaded');
      container.classList.add('error');
    }
  };

  // =====================================
  // DETECTION FUNCTIONS
  // =====================================

  const detectCircleShape = (cartItem) => {
    const textContent = cartItem.textContent || '';
    const innerHTML = cartItem.innerHTML || '';
    
    const hasCircleShape = textContent.includes('Circle Shape') || 
                          innerHTML.includes('Circle Shape') ||
                          textContent.includes('Circle shape') || 
                          innerHTML.includes('Circle shape');
    
    log(`🔍 Circle shape detected: ${hasCircleShape}`);
    return hasCircleShape;
  };

  const detectSingleSided = (cartItem) => {
    const textContent = cartItem.textContent || '';
    const innerHTML = cartItem.innerHTML || '';
    
    const isSingleSided = textContent.includes('Single Sided') || 
                         innerHTML.includes('Single Sided') ||
                         textContent.includes('Single sided') || 
                         innerHTML.includes('Single sided');
    
    log(`📄 Single sided detected: ${isSingleSided}`);
    return isSingleSided;
  };

  // =====================================
  // TOGGLE FUNCTIONALITY
  // =====================================

  const setupToggle = (container, itemNumber) => {
    log(`🔧 Setting up toggle for double-sided item ${itemNumber}`);

    container.onclick = (e) => {
      e.preventDefault();
      e.stopPropagation();

      if (container.dataset.toggling === 'true') {
        return;
      }

      const currentSide = parseInt(container.dataset.currentSide);
      const newSide = currentSide === 1 ? 2 : 1;
      
      log(`🔄 Toggling item ${itemNumber} from side ${currentSide} to side ${newSide}`);
      
      container.dataset.toggling = 'true';
      
      const frontCard = container.querySelector('.preview-card-front');
      const backCard = container.querySelector('.preview-card-back');
      
      if (frontCard && backCard) {
        // Switch classes
        if (newSide === 1) {
          frontCard.classList.add('front');
          frontCard.classList.remove('back');
          backCard.classList.add('back');
          backCard.classList.remove('front');
        } else {
          frontCard.classList.add('back');
          frontCard.classList.remove('front');
          backCard.classList.add('front');
          backCard.classList.remove('back');
        }

        container.dataset.currentSide = newSide;

        const sideBadgeLabel = container.querySelector('.preview-side-badge span');
        if (sideBadgeLabel) sideBadgeLabel.textContent = newSide === 1 ? 'Front' : 'Back';

        container.classList.add('switching');
        
        setTimeout(() => {
          container.classList.remove('switching');
          container.dataset.toggling = 'false';
        }, 400);
      }
    };
  };

  // =====================================
  // EDIT BUTTON FUNCTIONALITY - PRODUCTION
  // =====================================

  const addEditButtonToItem = (cartItem, projectId, variantId, itemNumber) => {
    // Check if edit button already exists
    if (cartItem.querySelector('.pitchprint-edit-btn-pill')) {
      return;
    }

    const buttonContainer = findEditButtonContainer(cartItem);
    if (!buttonContainer) {
      log(`❌ Could not find button container for item ${itemNumber}`);
      return;
    }

    const editButton = createEditButton(projectId, variantId, itemNumber);

    buttonContainer.appendChild(editButton);
    log(`✅ Edit button added for item ${itemNumber}`);
  };

  const findEditButtonContainer = (cartItem) => {
    // CRITICAL FIX: Check if edit container already exists before creating a new one
    let existingContainer = cartItem.querySelector('.pitchprint-edit-container');
    if (existingContainer) {
      return existingContainer;
    }

    // Liquid-first (PARITY-GAPS GAP 2): the enhanced action row is pre-rendered. Drop the
    // edit container straight into .enhanced-left-group so cart-offer-engine can wrap it in place.
    const enhancedLeftGroup = cartItem.querySelector('.enhanced-left-group');
    if (enhancedLeftGroup) {
      const editContainer = document.createElement('div');
      editContainer.className = 'pitchprint-edit-container';
      enhancedLeftGroup.appendChild(editContainer);
      return editContainer;
    }

    // Find the product-quantity element to insert BEFORE it (legacy/fallback layout)
    const quantityContainer = cartItem.querySelector('.product-quantity');

    if (quantityContainer) {
      // Create a container for the edit button
      const editContainer = document.createElement('div');
      editContainer.className = 'pitchprint-edit-container';
      editContainer.style.cssText = 'margin-bottom: 12px;';

      // Insert the container before the quantity element
      quantityContainer.parentNode.insertBefore(editContainer, quantityContainer);
      return editContainer;
    }

    // Fallback: try to find other containers
    const containerSelectors = [
      '.cart-item__actions',
      '.mini-cart__navigation .quantity-selector',
      '.cart-item__quantity-wrapper',
      '.cart-item__details',
      '.cart-item__content'
    ];

    for (const selector of containerSelectors) {
      const container = cartItem.querySelector(selector);
      if (container) {
        return container;
      }
    }

    // Create new container as fallback
    const newContainer = document.createElement('div');
    newContainer.className = 'pitchprint-actions';
    newContainer.style.cssText = 'margin-top: 8px; display: flex; gap: 8px; align-items: center;';

    const detailsContainer = cartItem.querySelector('.cart-item__details, .cart-item__content');
    if (detailsContainer) {
      detailsContainer.appendChild(newContainer);
      return newContainer;
    }

    cartItem.appendChild(newContainer);
    return newContainer;
  };

  const createEditButton = (projectId, variantId, itemNumber) => {
    log(`🔨 Creating edit button with projectId: "${projectId}", variantId: "${variantId}", itemNumber: ${itemNumber}`);

    const button = document.createElement('button');
    button.className = 'pitchprint-edit-btn-pill';
    button.type = 'button';
    button.title = 'Edit Design';
    button.setAttribute('data-project-id', projectId);
    button.setAttribute('data-variant-id', variantId);
    button.setAttribute('data-item-number', itemNumber);

    button.innerHTML = `
      <svg class="edit-icon" width="16" height="16" viewBox="0 0 75.194 75.192" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M60.656.009c-3.879,0-7.531,1.512-10.27,4.262L9.245,45.399C3.616,51.028.475,69.54.053,72.18c-.172.82.078,1.68.68,2.281.48.48,1.121.73,1.769.73.191,0,.371-.02.559-.059,2.828-.461,21.129-3.578,26.73-9.18l41.141-41.141c2.75-2.738,4.262-6.391,4.262-10.281s-1.512-7.531-4.262-10.27c-2.75-2.75-6.391-4.262-10.281-4.262l.005.009ZM67.398,21.279L26.257,62.42c-2.961,2.961-13.109,5.719-20.66,7.191,1.469-7.559,4.219-17.699,7.191-20.66L53.929,7.81c1.801-1.801,4.191-2.789,6.738-2.789s4.941.988,6.738,2.789c1.801,1.801,2.789,4.191,2.789,6.73s-.988,4.941-2.789,6.738h-.008Z" fill="currentColor"/>
      </svg>
      <span class="edit-text">Edit Design</span>
    `;

    button.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      log(`🖱️ Button clicked - using closure values: projectId="${projectId}", variantId="${variantId}"`);
      handleEditButtonClick(projectId, variantId, itemNumber);
    });

    return button;
  };

  const handleEditButtonClick = (projectId, variantId, itemNumber) => {
    log(`✏️ Edit button clicked for item ${itemNumber}`);
    log(`📊 Received parameters:`, { projectId, variantId, itemNumber });

    // Validate projectId
    if (!projectId || projectId === 'undefined' || projectId === 'null') {
      log('❌ Invalid project ID:', projectId);
      alert('Error: Cannot edit - invalid project ID');
      return;
    }

    log(`🎯 Redirecting to cart to edit project: ${projectId}`);

    // WORKAROUND SOLUTION: Redirect to cart page with projectId in URL
    // The ONLY reliable way to edit a saved project is to click the preview image on the cart page
    // We pass projectId as URL parameter so it works across devices/browsers/sessions
    // The cart page will automatically trigger the preview click

    const cartUrl = `/cart?pp_edit=${encodeURIComponent(projectId)}`;
    log('✅ Redirecting to:', cartUrl);

    window.location.href = cartUrl;
  };

  // =====================================
  // ENHANCED PROPERTY HIDING FOR PRODUCTION
  // =====================================

  const hidePitchPrintProperties = () => {
    log('🙈 Hiding PitchPrint properties from customer view...');

    // Note: Only hidden properties (_pitchprint, _pprint-pdf-download, _pp_system) are sent to cart now
    // Legacy visible properties (pitchprint, custom.pitchprint, pitchprojectid) have been removed
    const propertiesToHide = [
      '_pitchprint',
      '_pprint-pdf-download',
      '_pp_system',
      'pp_design_id',
      'pp_project'
    ];
    
    // Strategy 1: Hide dt/dd pairs in definition lists
    const definitionTerms = document.querySelectorAll(`
      .cart-item__details dt, 
      .mini-cart__navigation dt, 
      .line-item-properties dt,
      .cart-item-properties dt,
      .product-options dt
    `);
    
    definitionTerms.forEach(dt => {
      const text = dt.textContent.toLowerCase().trim();
      const shouldHide = propertiesToHide.some(prop => text.includes(prop.toLowerCase()));
      
      if (shouldHide) {
        dt.style.display = 'none';
        const dd = dt.nextElementSibling;
        if (dd && dd.tagName === 'DD') {
          dd.style.display = 'none';
        }
        log(`🙈 Hidden property: ${text}`);
      }
    });
    
    // Strategy 2: Hide property elements by class/data attributes
    const propertyElements = document.querySelectorAll(`
      .cart-item__property,
      .line-item-property,
      .cart-item-property,
      .product-option,
      [data-cart-item-property],
      [data-property-name],
      .property,
      .line-property
    `);
    
    propertyElements.forEach(element => {
      const text = element.textContent.toLowerCase();
      const dataProperty = element.dataset.cartItemProperty || element.dataset.propertyName || '';
      
      const shouldHide = propertiesToHide.some(prop => 
        text.includes(prop.toLowerCase()) || 
        dataProperty.toLowerCase().includes(prop.toLowerCase())
      );
      
      if (shouldHide) {
        element.style.display = 'none';
        log(`🙈 Hidden property element`);
      }
    });

    // Strategy 3: Hide by text content containing project IDs or PitchPrint terms
    const allElements = document.querySelectorAll(`
      .cart-item__details *, 
      .mini-cart__navigation *,
      .line-item-properties *,
      .cart-item-properties *
    `);
    
    allElements.forEach(element => {
      const text = element.textContent || '';
      
      // Hide elements whose entire text IS a project ID (32-40 hex chars, nothing else).
      // Using ^ and $ on trimmed text prevents false positives on filenames that
      // contain hex hash suffixes (e.g. PDF uploads like "file-1862938caeab8ae6.pdf").
      const hasProjectId = /^[a-f0-9]{32,40}$/.test(text.trim());
      
      // Hide elements with PitchPrint-specific terms
      const hasPitchPrintTerm = propertiesToHide.some(prop => 
        text.toLowerCase().includes(prop.toLowerCase())
      );
      
      if (hasProjectId || hasPitchPrintTerm) {
        // Don't hide parent containers, only leaf elements
        if (!element.children.length || element.children.length === 0) {
          element.style.display = 'none';
          log(`🙈 Hidden element with PitchPrint content`);
        }
      }
    });

    // Strategy 4: Hide specific form inputs that might be visible
    const formInputs = document.querySelectorAll(`
      input[name*="pitchprint"],
      input[name*="_pitchprint"],
      input[name*="pp_"]
    `);
    
    formInputs.forEach(input => {
      const container = input.closest('.cart-item__property, .line-item-property, .property');
      if (container) {
        container.style.display = 'none';
        log(`🙈 Hidden form input container`);
      } else {
        input.style.display = 'none';
        log(`🙈 Hidden form input`);
      }
    });
    
    log('✅ PitchPrint properties hidden from customer view');
  };

  // =====================================
  // CLEANUP
  // =====================================

  document.addEventListener('cartdrawer:closed', () => {
    cartIsOpen = false;
    log('🛒 Cart closed');
  });

  // Error boundary
  window.addEventListener('error', (e) => {
    if (e.message.includes('mini-cart') || e.filename?.includes('mini-cart')) {
      stateManager.logError(e.error, 'miniCartPreviewsFixedError');
    }
  });

  // =====================================
  // INITIALIZATION
  // =====================================

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    setTimeout(init, 0);
  }

  log('🎨 FIXED Mini cart previews system loaded and ready');

  // Debug helpers
  window.debugMiniCartPreviews = {
    inspectCartItems: async () => {
      const cartItems = document.querySelectorAll('.mini-cart__navigation .cart-item, .mini-cart__navigation [data-variant-id]');
      console.log(`Found ${cartItems.length} cart items`);
      for (const [index, item] of cartItems.entries()) {
        const cartItemData = await getCartItemData(item);
        console.log(`Item ${index + 1}:`, {
          variantId: item.dataset.variantId,
          cartItemData: cartItemData,
          textPreview: item.textContent.substring(0, 100)
        });
      }
    },

    getProcessedItems: () => processedItems,
    forceProcess: () => processCartItems(),
    clearProcessed: () => processedItems.clear(),
    forceHideProperties: () => hidePitchPrintProperties()
  };
})();